import React, { useState } from 'react';
import { Lock, Share2 } from 'lucide-react';
import { Card, CardContent } from '../../@/components/ui/card';
import { Button } from '../../@/components/ui/button';
import { Icon } from '@iconify/react';

const lessonLevelBadges = [
  { icon: "fluent-emoji:star" },
  { icon: "fluent-emoji:gem-stone" },
  { icon: "fluent-emoji:trophy" },
  { icon: "fluent-emoji:ribbon" },
  { icon: "fluent-emoji:crown" },
];

const ProgressBar = ({ value }) => (
  <div className="w-full h-1.5 rounded-full overflow-hidden bg-(--neutral-200)">
    <div
      className="h-full rounded-full transition-all duration-300"
      style={{ width: `${value}%`, backgroundColor: 'var(--color-success)' }}
    ></div>
  </div>
);

const BadgeCard = ({ title, totalLessons, completedLessons, status, level = 1 }) => {
  const [shared, setShared] = useState(false);
  const progress = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;

  const handleShare = () => {
    setShared(true);
    setTimeout(() => setShared(false), 2000);
  };

  const levelIndex = Math.max(0, Math.min(level - 1, lessonLevelBadges.length - 1));
  const unlockedBadge = lessonLevelBadges[levelIndex];

  if (status === 'completed') {
    return (
      <Card className="w-full max-w-48 border border-(--neutral-300) bg-(--neutral-50) transition-shadow">
        <CardContent className="flex flex-col items-center pt-4 pb-3">
          <div className="w-20 h-20 flex items-center justify-center mb-3">
            <Icon icon={unlockedBadge.icon} width="72" height="72" />
          </div>
          <div className="mt-2 text-center">
            <h3 className="text-lg font-semibold text-(--neutral-900) mb-1">{title}</h3>
            <p className="text-xs text-(--neutral-600) mb-2">Complete {totalLessons} lessons</p>
            <Button onClick={handleShare} className="w-full text-sm font-semibold py-0" variant="link">
              {shared ? 'Shared!' : 'Share Badge'}
              <Share2 className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === 'in-progress') {
    return (
      <Card className="w-full max-w-48 border border-(--neutral-300) bg-(--neutral-50)">
        <CardContent className="flex flex-col items-center pt-4 pb-3">
          <div className="w-20 h-20 flex items-center justify-center mb-3">
            <Icon icon={unlockedBadge.icon} width="60" height="60" />
          </div>
          <div className="mt-1.5 text-center">
            <h3 className="text-lg font-semibold text-(--neutral-900) mb-1">{title}</h3>
            <p className="text-xs text-(--neutral-600) mb-2">Complete {totalLessons} lessons</p>
            <div className="w-full">
              <ProgressBar value={progress} />
              <div className="flex justify-between items-center text-[10px] mt-1.5">
                <span className="text-(--neutral-600)">{completedLessons}/{totalLessons} lessons</span>
                <span className="text-(--color-success) font-semibold">Earn +50 XP</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-48 border border-(--neutral-300) bg-(--neutral-100) shadow-md opacity-70">
      <CardContent className="flex flex-col items-center pt-4 pb-3">
        <div className="w-20 h-20 flex items-center justify-center mb-3">
          <Icon icon="mdi:lock" width="64" height="64" className="text-(--neutral-500)" />
        </div>
        <h3 className="text-lg font-semibold text-(--neutral-600) mb-1">{title}</h3>
        <p className="text-xs text-(--neutral-500) mb-2">Complete {totalLessons} lessons</p>
        <div className="flex items-center gap-1.5 bg-(--neutral-500) text-(--neutral-50) px-3 py-1.5 rounded-full text-xs font-semibold">
          <Lock className="w-3.5 h-3.5" strokeWidth={2} />
          LEARNING
        </div>
      </CardContent>
    </Card>
  );
};

const LessonExplorerBadges = () => {
  const badges = [
    { id: 1, title: 'Beginner', totalLessons: 10, completedLessons: 10, status: 'completed', level: 1 },
    { id: 2, title: 'Rookie', totalLessons: 10, completedLessons: 7, status: 'in-progress', level: 2 },
    { id: 3, title: 'Amateur', totalLessons: 10, completedLessons: 5, status: 'in-progress', level: 3 },
    { id: 4, title: 'Pro', totalLessons: 10, completedLessons: 3, status: 'in-progress', level: 4 },
    { id: 5, title: 'Master', totalLessons: 10, completedLessons: 1, status: 'in-progress', level: 5 },
  ];

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {badges.map((badge) => (
            <BadgeCard
              key={badge.id}
              title={badge.title}
              totalLessons={badge.totalLessons}
              completedLessons={badge.completedLessons}
              status={badge.status}
              level={badge.level}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LessonExplorerBadges;